import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { fetchPracticeSummary, fetchPractice } from '../../../store/slices/practiceSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserMd,
  faUsers,
  faUserNurse,
  faUserInjured,
  faCalendarAlt,
  faFileImport,
  faUserCheck,
  faLink,
  faHeart,
  faChartLine,
  faList
} from '@fortawesome/free-solid-svg-icons';
import PracticeDetails from '../PracticeDetails/PracticeDetails';
import AdminManagement from '../AdminManagement/AdminManagement';
import ProviderManagement from '../ProviderManagement/ProviderManagement';
import CaregiverManagement from '../CaregiverManagement/CaregiverManagement';
import PatientManagement from '../PatientManagement/PatientManagement';
import SystemCaregiverManagement from '../SystemCaregiverManagement/SystemCaregiverManagement';
import ScheduleTargetSettings from '../ScheduleTargetSettings/ScheduleTargetSettings';
import ImportPatients from '../ImportPatients/ImportPatients';
import './PracticeDashboard.css';
import { buildProgramRibbonItems } from '../../../utils/careProgramUtils';
import { resolvePracticeLogoUrl } from '../../../utils/assetUrls';
import { usePracticeDashboardTabIndicator } from './usePracticeDashboardTabIndicator';
import { useMountedDashboardTabs } from './useMountedDashboardTabs';
import PracticeDashboardTabPane from './PracticeDashboardTabPane';
import PracticeInlineEventLogs from './PracticeInlineEventLogs';
import {
  getPracticeTabEventLogModule,
  PRACTICE_EVENT_LOGS_QUERY,
} from './practiceEventLogs';
import { isEvitalsMonitoringPractice, getPracticeMonitoringSectionFlags } from '../../../utils/practiceMonitoringUtils';
import { ROLES, getRoleId } from '../../../utils/roleUtils';
import { formatDateOnlyDash } from '../../../utils/dateOnlyUtils';

/**
 * Local ErrorBoundary to avoid adding external deps.
 * Shows a visible fallback instead of leaving a blank area when a child throws.
 */
class LocalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // keep logging so devs can see stack traces in console
    console.error('[LocalErrorBoundary] caught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
    // optionally call parent's onReset if provided
    if (typeof this.props.onReset === 'function') {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.error) {
      const Fallback = this.props.FallbackComponent;
      // render user-provided fallback or minimal fallback
      if (Fallback) {
        return <Fallback error={this.state.error} resetErrorBoundary={this.reset} />;
      }
      return (
        <div style={{ padding: 12, border: '1px solid #e74c3c', background: '#fff5f5', borderRadius: 6 }}>
          <strong>Something went wrong.</strong>
          <div style={{ marginTop: 8, fontSize: 13 }}>{this.state.error?.message || 'Unknown error'}</div>
          <div style={{ marginTop: 8 }}>
            <button className="btn btn-sm btn-outline-secondary" onClick={this.reset}>Retry</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* Visible fallback UI for schedule tab errors */
const ScheduleErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div style={{
      border: '1px solid #e74c3c',
      background: '#fff5f5',
      color: '#a94442',
      padding: 12,
      borderRadius: 6,
      margin: 8
    }}>
      <strong>Schedule UI failed to render.</strong>
      <div style={{ marginTop: 6, fontSize: 13 }}>
        {error?.message || 'Unknown render error in ScheduleTargetSettings'}
      </div>
      <div style={{ marginTop: 8 }}>
        <button className="btn btn-sm btn-outline-secondary" onClick={resetErrorBoundary}>
          Retry
        </button>
      </div>
    </div>
  );
};

const isPracticeLockedStatus = (status) => {
  if (status == null) return false;
  if (typeof status === 'number') return status === 4;
  const normalized = String(status).trim().toLowerCase();
  return normalized === '4' || normalized === 'locked';
};

const PracticeDashboard = () => {
  const { practiceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth); // Get user for role check
  const { currentPractice } = useSelector(state => state.practice ?? {});
  const dispatch = useDispatch();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  // Practice data from Redux store
  const [practiceData, setPracticeData] = useState(() => {
    if (!currentPractice) return null;
    return String(currentPractice.id) === String(practiceId) ? currentPractice : null;
  });
  const [loading, setLoading] = useState(() => {
    if (!currentPractice) return true;
    return String(currentPractice.id) !== String(practiceId);
  });
  const tabContentRef = useRef(null);
  const practiceViewLoggedRef = useRef(false);

  // All tabs (including previously in More dropdown)
  const requiresSystemCaregiverTab = isEvitalsMonitoringPractice(practiceData);
  const tabs = useMemo(() => {
    const allTabs = [
      { id: 'dashboard', label: 'Practice', icon: faChartLine },
      { id: 'admin', label: 'Admins', icon: faUsers },
      { id: 'provider', label: 'Providers', icon: faUserMd },
      { id: 'caregiver', label: 'Caregivers', icon: faUserNurse },
      { id: 'patient', label: 'Patients', icon: faUserInjured },
      { id: 'system-caregiver', label: 'System Caregivers', icon: faUserCheck },
      { id: 'schedule', label: 'Schedule & Targets', icon: faCalendarAlt },
      { id: 'import', label: 'Import Patients', icon: faFileImport }
    ];
    if (requiresSystemCaregiverTab) return allTabs;
    return allTabs.filter((tab) => tab.id !== 'system-caregiver');
  }, [requiresSystemCaregiverTab]);


  const getTabFromSearch = useCallback((search) => {
    if (getRoleId(user) === ROLES.SYSTEM_CAREGIVER) return 'patient';

    const params = new URLSearchParams(search);
    const requested = params.get('tab');

    // Support 'dashboard' as a valid tab, mapping it internally if needed
    if (requested === 'dashboard') return 'dashboard';
    if (requested === 'system-caregiver' && !requiresSystemCaregiverTab) return 'dashboard';

    return tabs.some(tab => tab.id === requested) ? requested : 'dashboard';
  }, [tabs, user, requiresSystemCaregiverTab]);


  const [searchParams] = useSearchParams();
  const viewId = searchParams.get('viewId');
  const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));
  const { tabsWrapRef, tabIndicatorStyle, indicatorReady } = usePracticeDashboardTabIndicator(
    activeTab,
    tabs.length
  );
  const mountedTabs = useMountedDashboardTabs(activeTab, practiceId);
  const selectedProgramId = useMemo(() => {
    const programParam = String(searchParams.get('program') || '').trim().toLowerCase();
    return activeTab === 'patient' && programParam ? programParam : '';
  }, [searchParams, activeTab]);

  const scheduleLogSection = useMemo(() => {
    if (activeTab !== 'schedule') return null;
    const section = String(searchParams.get('scheduleLogSection') || '').trim();
    return section || null;
  }, [searchParams, activeTab]);

  const openScheduleEventLog = useCallback(
    (section) => {
      const params = new URLSearchParams(location.search);
      params.set('tab', 'schedule');
      // 'all' opens full Schedule event logs; a specific section filters the list.
      // Never delete the param here — that left the Event Logs button doing nothing.
      params.set('scheduleLogSection', section || 'all');
      params.delete(PRACTICE_EVENT_LOGS_QUERY);
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true, state: location.state }
      );
    },
    [location.pathname, location.search, location.state, navigate]
  );

  const eventLogsActive = searchParams.get(PRACTICE_EVENT_LOGS_QUERY) === '1';

  const openTabEventLogs = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', activeTab);
    params.set(PRACTICE_EVENT_LOGS_QUERY, '1');
    params.delete('viewId');
    params.delete('scheduleLogSection');
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true, state: location.state }
    );
  }, [activeTab, location.pathname, location.search, location.state, navigate]);

  const renderWithInlineEventLogs = useCallback((tabId, content) => {
    const moduleId = getPracticeTabEventLogModule(tabId);
    if (eventLogsActive && activeTab === tabId && moduleId) {
      return (
        <PracticeInlineEventLogs
          moduleId={moduleId}
          practiceId={practiceId}
        />
      );
    }
    return content;
  }, [eventLogsActive, activeTab, practiceId]);

  // Entity data for breadcrumb lookup
  const { adminsByPracticeId } = useSelector(state => state.admin ?? {});
  const { providersByPracticeId, caregiversByPracticeId } = useSelector(state => state.practice ?? {});
  const { systemCaregivers } = useSelector(state => state.systemCaregiver ?? {});

  const getEntityName = useCallback((tab, id) => {
    if (!id) return null;
    let list = [];
    const pid = String(practiceId);

    if (tab === 'admin') list = adminsByPracticeId[pid] || [];
    else if (tab === 'provider') list = providersByPracticeId[pid] || [];
    else if (tab === 'caregiver') list = caregiversByPracticeId[pid] || [];
    else if (tab === 'system-caregiver') list = systemCaregivers || [];

    const entity = list.find(e => String(e.id) === String(id));
    if (!entity) return null;

    if (entity.name) return entity.name;
    if (entity.first_name || entity.last_name) {
      return `${entity.first_name || ''} ${entity.last_name || ''}`.trim();
    }
    return null;
  }, [practiceId, adminsByPracticeId, providersByPracticeId, caregiversByPracticeId, systemCaregivers]);

  // Sync tab from URL only — do not depend on activeTab (avoids flash back to Dashboard on click)
  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlTab = params.get('tab');
    const resolvedTab = getTabFromSearch(location.search);

    if (urlTab && urlTab !== resolvedTab) {
      params.set('tab', resolvedTab);
      params.delete('viewId');
      if (resolvedTab !== 'patient') params.delete('program');
      if (resolvedTab !== 'schedule') params.delete('scheduleLogSection');
      params.delete(PRACTICE_EVENT_LOGS_QUERY);
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true, state: location.state }
      );
      setActiveTab(resolvedTab);
      return;
    }

    if (urlTab && tabs.some((tab) => tab.id === urlTab)) {
      setActiveTab((prev) => (prev === urlTab ? prev : urlTab));
      return;
    }

    if (!urlTab) {
      params.set('tab', 'dashboard');
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true, state: location.state }
      );
    }
  }, [location.search, location.pathname, navigate, tabs, location.state, getTabFromSearch]);

  useEffect(() => {
    practiceViewLoggedRef.current = false;
  }, [practiceId]);

  useEffect(() => {
    const loadPracticeData = async () => {
      if (!practiceId) return;
      const shouldAuditView = !practiceViewLoggedRef.current;
      if (shouldAuditView) {
        practiceViewLoggedRef.current = true;
      }
      try {
        setLoading(true);
        const result = await dispatch(fetchPracticeSummary({
          id: practiceId,
          auditView: shouldAuditView
        }));
        if (fetchPracticeSummary.fulfilled.match(result)) {
          setPracticeData(result.payload.data);
        } else {
          if (shouldAuditView) practiceViewLoggedRef.current = false;
          console.error('Failed to load practice:', result.payload);
        }
      } catch (error) {
        if (shouldAuditView) practiceViewLoggedRef.current = false;
        console.error('Error loading practice:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPracticeData();
  }, [practiceId, dispatch]);

  useEffect(() => {
    if (!practiceId) return undefined;
    const refreshCounts = async (event) => {
      const changedId = event?.detail?.practiceId;
      if (changedId != null && String(changedId) !== String(practiceId)) return;
      const sum = await dispatch(fetchPracticeSummary(practiceId));
      if (fetchPracticeSummary.fulfilled.match(sum)) {
        setPracticeData(sum.payload.data);
      }
    };
    window.addEventListener('evitals:practice-patients-changed', refreshCounts);
    return () => window.removeEventListener('evitals:practice-patients-changed', refreshCounts);
  }, [practiceId, dispatch]);

  // After edit practice (lock/unlock etc.), refresh summary + Redux practice without full page reload
  useEffect(() => {
    if (!practiceId || !location.state?.practiceUpdated) return;
    let cancelled = false;
    (async () => {
      await dispatch(fetchPractice(practiceId));
      const sum = await dispatch(fetchPracticeSummary(practiceId));
      if (!cancelled && fetchPracticeSummary.fulfilled.match(sum)) {
        setPracticeData(sum.payload.data);
      }
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: { ...location.state, practiceUpdated: undefined } }
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceId, dispatch, navigate, location.pathname, location.search, location.state?.practiceUpdated]);

  // Update breadcrumbs when practice data loads
  useEffect(() => {
    if (practiceData) {
      const isProvider = Number(user?.role_id) === 4;
      const isPracticeAdmin = Number(user?.role_id) === 3;
      const hideGlobalPracticesCrumb = isProvider || isPracticeAdmin;
      const currentTab = tabs.find(tab => tab.id === activeTab);
      const isFromDashboardPath = location.pathname.startsWith('/dashboard') || location.state?.fromDashboard;
      const rootLabel = isFromDashboardPath ? 'Dashboard' : 'Practices';
      const rootPath = isFromDashboardPath ? '/dashboard' : '/practices';

      const practiceCrumbPath =
        activeTab === 'practice' || activeTab === 'dashboard'
          ? null
          : `${location.pathname}?tab=dashboard`;

      const baseCrumbs = [];

      if (activeTab === 'practice' || activeTab === 'dashboard') {
        if (hideGlobalPracticesCrumb) {
          // Practice-scoped roles: hide global list crumb; show practice name only.
          if (eventLogsActive) {
            updateBreadcrumbs([
              { label: practiceData.practice_name, path: `${location.pathname}?tab=dashboard`, active: false },
              { label: 'Event Logs', path: null, active: true }
            ]);
            return;
          }
          updateBreadcrumbs([]);
          if (isPracticeAdmin) {
            updateBreadcrumbs([
              { label: practiceData.practice_name, path: null, active: true }
            ]);
          }
          return;
        }
        baseCrumbs.push({ label: rootLabel, path: rootPath, active: false });
        if (eventLogsActive) {
          baseCrumbs.push({
            label: practiceData.practice_name,
            path: `${location.pathname}?tab=dashboard`,
            active: false
          });
          baseCrumbs.push({ label: 'Event Logs', path: null, active: true });
        } else {
          baseCrumbs.push({ label: practiceData.practice_name, path: null, active: true });
        }
      } else {
        if (!hideGlobalPracticesCrumb) {
          baseCrumbs.push({ label: rootLabel, path: rootPath, active: false });
        }
        // Clicking practice name returns to the Practice tab (even for practice-scoped roles).
        baseCrumbs.push({
          label: practiceData.practice_name,
          path: practiceCrumbPath,
          active: false,
          state: { fromDashboard: isFromDashboardPath }
        });

        const tabPath = `${location.pathname}?tab=${activeTab}`;
        const entityName = getEntityName(activeTab, viewId);
        if (eventLogsActive) {
          baseCrumbs.push({
            label: currentTab?.label || 'Practice',
            path: tabPath,
            active: false,
            state: { fromDashboard: isFromDashboardPath }
          });
          baseCrumbs.push({ label: 'Event Logs', path: null, active: true });
        } else if (activeTab === 'schedule' && scheduleLogSection) {
          // Drop scheduleLogSection so breadcrumb opens Schedule & Targets settings again
          baseCrumbs.push({
            label: currentTab?.label || 'Schedule & Targets',
            path: `${location.pathname}?tab=schedule`,
            active: false,
            state: { fromDashboard: isFromDashboardPath }
          });
          baseCrumbs.push({ label: 'Event Logs', path: null, active: true });
        } else if (entityName) {
          baseCrumbs.push({ label: currentTab?.label || 'Practice', path: tabPath, active: false, state: { fromDashboard: isFromDashboardPath } });
          baseCrumbs.push({ label: entityName, path: null, active: true });
        } else {
          baseCrumbs.push({ label: currentTab?.label || 'Practice', path: null, active: true });
        }
      }

      updateBreadcrumbs(baseCrumbs);
    }
  }, [practiceData, activeTab, tabs, updateBreadcrumbs, location.pathname, viewId, getEntityName, user, eventLogsActive, scheduleLogSection]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  const practiceLogoUrl = useMemo(() => {
    return resolvePracticeLogoUrl(practiceData?.practice_logo, {
      cacheBust: practiceData?.updated_at || practiceData?.id || 'practice-logo'
    });
  }, [practiceData?.practice_logo, practiceData?.updated_at, practiceData?.id]);

  const [practiceLogoFailed, setPracticeLogoFailed] = useState(false);
  useEffect(() => {
    setPracticeLogoFailed(false);
  }, [practiceLogoUrl]);

  /** Match legacy stack: MM-DD-YYYY (e.g. 03-01-2026) */
  const formatBillingPeriodDate = (iso) => {
    if (!iso) return 'N/A';
    return formatDateOnlyDash(iso) || 'N/A';
  };

  const normalizeProgramKey = (name) => {
    if (!name) return '';
    return String(name).trim().toLowerCase().replace(/\s+/g, '-');
  };

  const selectedProgramRange = useMemo(() => {
    if (!selectedProgramId || !Array.isArray(practiceData?.enrolled_programs)) return null;
    return practiceData.enrolled_programs
      .map((program) => {
        if (!program) return null;
        const programName = typeof program === 'string'
          ? program
          : String(program.short_name || program.id || program.full_name || program.program || program.label || '').trim();
        if (!programName) return null;
        return {
          key: normalizeProgramKey(programName),
          name: programName,
          start_date: typeof program === 'string'
            ? null
            : program.start_date || program.rpm_start_date || program.startDate || null,
          end_date: typeof program === 'string'
            ? null
            : program.end_date || program.rpm_end_date || program.endDate || null
        };
      })
      .filter(Boolean)
      .find((range) => range.key === selectedProgramId) || null;
  }, [selectedProgramId, practiceData?.enrolled_programs]);

  const programEnrollmentRanges = useMemo(() => {
    const enrolled = Array.isArray(practiceData?.enrolled_programs) ? practiceData.enrolled_programs : [];
    return enrolled
      .map((program) => {
        if (!program) return null;

        const programName = typeof program === 'string'
          ? program
          : String(program.short_name || program.id || program.full_name || program.program || program.label || '').trim();

        if (!programName) return null;

        return {
          id: programName,
          name: programName,
          start_date: typeof program === 'string'
            ? null
            : program.start_date || program.rpm_start_date || program.startDate || null,
          end_date: typeof program === 'string'
            ? null
            : program.end_date || program.rpm_end_date || program.endDate || null
        };
      })
      .filter(Boolean);
  }, [practiceData?.enrolled_programs]);

  const renderProgramDates = () => {
    if (!selectedProgramId || !selectedProgramRange) {
      return null;
    }

    return (
      <div className="practice-dashboard-billing-periods">
        <div key={selectedProgramRange.key} className="practice-dashboard-period-row">
          <span className="practice-dashboard-period-value">
            {formatBillingPeriodDate(selectedProgramRange.start_date)} - {formatBillingPeriodDate(selectedProgramRange.end_date)}
          </span>
        </div>
      </div>
    );
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(location.search);
    params.set('tab', tabId);
    params.delete('viewId');
    if (tabId !== 'patient') {
      params.delete('program');
    }
    // Always clear so clicking Schedule & Targets (or leaving it) returns to settings, not event logs
    params.delete('scheduleLogSection');
    params.delete(PRACTICE_EVENT_LOGS_QUERY);
    navigate({
      pathname: location.pathname,
      search: params.toString()
    }, { replace: true, state: location.state });
  };

  const isPracticeLocked = useMemo(
    () => isPracticeLockedStatus(practiceData?.status),
    [practiceData?.status]
  );

  const renderTabContent = () => (
    <div className="practice-dashboard-tab-panel">
      <PracticeDashboardTabPane tabId="dashboard" activeTab={activeTab} mountedTabs={mountedTabs}>
        {renderWithInlineEventLogs(
          'dashboard',
          <PracticeDetails
            practiceData={practiceData}
            embeddedInPracticeDashboard
            practiceId={practiceId}
            onOpenEventLogs={openTabEventLogs}
          />
        )}
      </PracticeDashboardTabPane>
      <PracticeDashboardTabPane tabId="admin" activeTab={activeTab} mountedTabs={mountedTabs}>
        {renderWithInlineEventLogs(
          'admin',
          <AdminManagement
            practiceId={practiceId}
            isReadOnly={isPracticeLocked}
            onOpenEventLogs={openTabEventLogs}
            hideEventLogsButton
          />
        )}
      </PracticeDashboardTabPane>
      <PracticeDashboardTabPane tabId="provider" activeTab={activeTab} mountedTabs={mountedTabs}>
        {renderWithInlineEventLogs(
          'provider',
          <ProviderManagement
            practiceId={practiceId}
            isReadOnly={isPracticeLocked}
            onOpenEventLogs={openTabEventLogs}
            hideEventLogsButton
          />
        )}
      </PracticeDashboardTabPane>
      <PracticeDashboardTabPane tabId="caregiver" activeTab={activeTab} mountedTabs={mountedTabs}>
        {renderWithInlineEventLogs(
          'caregiver',
          <CaregiverManagement
            practiceId={practiceId}
            isReadOnly={isPracticeLocked}
            onOpenEventLogs={openTabEventLogs}
            hideEventLogsButton
          />
        )}
      </PracticeDashboardTabPane>
      <PracticeDashboardTabPane tabId="patient" activeTab={activeTab} mountedTabs={mountedTabs}>
        {renderWithInlineEventLogs(
          'patient',
          <PatientManagement
            practiceId={practiceId}
            isReadOnly={isPracticeLocked}
            embeddedInPracticeDashboard
            dashboardTabKey="patient"
            isDashboardTabActive={activeTab === 'patient'}
            onOpenEventLogs={openTabEventLogs}
            hideEventLogsButton
          />
        )}
      </PracticeDashboardTabPane>
      {requiresSystemCaregiverTab && (
      <PracticeDashboardTabPane tabId="system-caregiver" activeTab={activeTab} mountedTabs={mountedTabs}>
        {renderWithInlineEventLogs(
          'system-caregiver',
          <SystemCaregiverManagement
            practiceId={practiceId}
            isReadOnly={isPracticeLocked}
            onOpenEventLogs={openTabEventLogs}
            hideEventLogsButton
          />
        )}
      </PracticeDashboardTabPane>
      )}
      <PracticeDashboardTabPane tabId="schedule" activeTab={activeTab} mountedTabs={mountedTabs}>
        <div style={{ display: 'block' }}>
          <LocalErrorBoundary FallbackComponent={ScheduleErrorFallback}>
            {scheduleLogSection ? (
              <PracticeInlineEventLogs
                moduleId="Schedule"
                practiceId={practiceId}
                sectionFilter={scheduleLogSection === 'all' ? null : scheduleLogSection}
              />
            ) : (
              <ScheduleTargetSettings
                practiceId={practiceId}
                isReadOnly={isPracticeLocked}
                showSectionEventLogButtons={false}
                onOpenEventLog={openScheduleEventLog}
                isParentTabActive={activeTab === 'schedule' && !scheduleLogSection}
                enabledMonitoring={getPracticeMonitoringSectionFlags(practiceData?.selected_monitoring_types)}
              />
            )}
          </LocalErrorBoundary>
        </div>
      </PracticeDashboardTabPane>
      <PracticeDashboardTabPane tabId="import" activeTab={activeTab} mountedTabs={mountedTabs}>
        <ImportPatients practiceId={practiceId} isReadOnly={isPracticeLocked} />
      </PracticeDashboardTabPane>
    </div>
  );

  if (loading) {
    return (
      <div className="practice-dashboard-loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!practiceData) {
    return (
      <div className="practice-dashboard-alert practice-dashboard-alert-danger" role="alert">
        Practice not found!
      </div>
    );
  }

  const renderProgramSummary = () => {
    const enrolled = Array.isArray(practiceData.enrolled_programs) ? practiceData.enrolled_programs : [];
    if (enrolled.length === 0) {
      return (
        <div className="practice-dashboard-program-ribbon">
          <span className="program-ribbon-text text-muted">No care programs enrolled</span>
        </div>
      );
    }

    const programs = buildProgramRibbonItems(enrolled, practiceData.program_counts || {});

    return (
      <div className="practice-dashboard-program-ribbon">
        {programs.map((prog) => (
          <div key={prog.id} className="program-ribbon-item">
            <FontAwesomeIcon icon={prog.icon} className="program-ribbon-icon" style={{ color: prog.color }} />
            <span className="program-ribbon-text">
              <span className="program-ribbon-count">{prog.count}</span>{' '}
              {prog.short_name || prog.displayName || prog.full_name || prog.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const hasPracticeLogo = Boolean(practiceLogoUrl && !practiceLogoFailed);
  const showBillingInHeader = activeTab === 'patient' && selectedProgramRange;
  const showHeaderRight = hasPracticeLogo || showBillingInHeader;

  return (
    <div className="practice-dashboard">
      <div className="practice-dashboard-single-card">
        <section className="practice-dashboard-card-header">
          <div className="practice-dashboard-header-main">
            <div className="practice-dashboard-header-left">
              <h2 className="practice-dashboard-title">{practiceData.practice_name}</h2>
              {renderProgramSummary()}
            </div>
            {showHeaderRight ? (
              <div className="practice-dashboard-header-right">
                {hasPracticeLogo && activeTab === 'dashboard' && (
                  <div className="practice-dashboard-logo-wrap">
                    <img
                      src={practiceLogoUrl}
                      alt={`${practiceData.practice_name || 'Practice'} logo`}
                      className="practice-dashboard-logo-img"
                      onError={() => setPracticeLogoFailed(true)}
                    />
                  </div>
                )}
                {showBillingInHeader && (
                  <div className="practice-dashboard-billing-info">
                    <span className="billing-label" style={{ textTransform: 'uppercase' }}>
                      {selectedProgramRange.name} BILLING DATES
                    </span>
                    {renderProgramDates()}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {isPracticeLocked && (
            <div className="alert alert-warning mt-2 mb-0" role="alert" style={{ fontSize: '13px', padding: '8px 12px' }}>
              This practice is locked. User lists are available in view-only mode.
            </div>
          )}
        </section>

        {user?.role_id !== 7 && (
          <nav className="practice-dashboard-nav" aria-label="Practice sections">
            <div className="practice-dashboard-tabs-wrap" ref={tabsWrapRef}>
              <span
                className={`practice-dashboard-tab-indicator${indicatorReady ? ' practice-dashboard-tab-indicator--ready' : ''}`}
                style={tabIndicatorStyle}
                aria-hidden="true"
              />
              <ul className="practice-dashboard-tabs">
                {tabs.map((tab) => (
                  <li key={tab.id} className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}>
                    <button
                      onClick={() => handleTabChange(tab.id)}
                      className="tab-button"
                      type="button"
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        )}

        <section className="practice-dashboard-card-body" ref={tabContentRef}>
          {renderTabContent()}
        </section>
      </div>
      {['admin', 'provider', 'caregiver', 'patient', 'system-caregiver', 'schedule'].includes(activeTab)
        && !(activeTab === 'schedule' && scheduleLogSection) && (
          <div className="practice-dashboard-event-log-actions">
            <button
              type="button"
              className="admin-management-events-log-btn"
              onClick={() => (activeTab === 'schedule' ? openScheduleEventLog(null) : openTabEventLogs())}
            >
              <FontAwesomeIcon icon={faList} className="admin-management-add-icon" /> Event Logs
            </button>
          </div>
        )}
    </div>
  );
};

export default PracticeDashboard;
